import { useState, useCallback, useRef, useEffect } from 'react';
import { Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from '@/components/ui/context-menu';
import type { WorkflowSheet, CrossSheetConnector } from '@/types/workflow';
import { getSheetDisplayName, stripCirclePrefix } from '@/lib/sheetNaming';

interface SheetTabBarProps {
  sheets: WorkflowSheet[];
  activeSheetIndex: number;
  crossSheetConnectors: CrossSheetConnector[];
  maxSheets: number;
  onSetActiveSheet: (index: number) => void;
  onAddSheet: () => void;
  onRemoveSheet: (index: number) => void;
  onRenameSheet: (index: number, name: string) => void;
  onDuplicateSheet: (index: number) => void;
  onReorderSheets: (from: number, to: number) => void;
  onUpdateSheetMeta: (index: number, meta: { color?: string }) => void;
  /**
   * Read-only mode (used by /preview route): tab-switching via click
   * stays enabled, but rename/add/remove/reorder/duplicate UI is hidden.
   * Double-click rename + context-menu + add-button all become no-ops.
   */
  readOnly?: boolean;
}

export function SheetTabBar({
  sheets, activeSheetIndex, crossSheetConnectors, maxSheets,
  onSetActiveSheet, onAddSheet, onRemoveSheet, onRenameSheet,
  onDuplicateSheet, onReorderSheets,
  readOnly = false,
}: SheetTabBarProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const canAdd = maxSheets < 0 || sheets.length < maxSheets;

  useEffect(() => {
    if (editingIndex !== null) inputRef.current?.focus();
  }, [editingIndex]);

  const startRename = useCallback((index: number) => {
    setEditingIndex(index);
    // Visa bara user-text-delen i input — sifferprefix är FAST och kan
    // inte redigeras. Strip:a ev. legacy-prefix från lagrat namn.
    setEditValue(stripCirclePrefix(sheets[index]?.name ?? '').trim());
  }, [sheets]);

  const commitRename = useCallback(() => {
    // Tillåt tom rename → sheet visas bara som sifferprefix (t.ex. "②").
    // Sifferprefix kan inte tas bort; user kontrollerar bara text-delen.
    if (editingIndex !== null) {
      onRenameSheet(editingIndex, editValue);
    }
    setEditingIndex(null);
  }, [editingIndex, editValue, onRenameSheet]);

  const getSheetConnectorDirections = useCallback((sheetIndex: number) => {
    let hasUp = false;
    let hasDown = false;
    for (const c of crossSheetConnectors) {
      if (c.fromSheetIndex === sheetIndex && c.toSheetIndex === sheetIndex - 1) hasUp = true;
      if (c.toSheetIndex === sheetIndex && c.fromSheetIndex === sheetIndex - 1) hasUp = true;
      if (c.fromSheetIndex === sheetIndex && c.toSheetIndex === sheetIndex + 1) hasDown = true;
      if (c.toSheetIndex === sheetIndex && c.fromSheetIndex === sheetIndex + 1) hasDown = true;
    }
    return { hasUp, hasDown };
  }, [crossSheetConnectors]);

  return (
    <div
      className="h-9 flex items-center gap-0.5 px-2 border-b border-border bg-muted/30 flex-shrink-0 overflow-x-auto"
      role="tablist"
      aria-label="Workflow sheets"
      data-guide="sheet-tabs"
      style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}
    >
      {sheets.map((sheet, index) => {
        const isActive = index === activeSheetIndex;
        const { hasUp, hasDown } = getSheetConnectorDirections(index);
        const isEditing = editingIndex === index;

        return (
          <ContextMenu key={sheet.id}>
            <ContextMenuTrigger asChild disabled={readOnly}>
              <button
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={() => !isEditing && onSetActiveSheet(index)}
                onDoubleClick={() => { if (!readOnly) startRename(index); }}
                onKeyDown={(e) => {
                  // #78: tablist keyboard nav. Skipa i rename-läge (input
                  // äger keystrokes då).
                  if (isEditing) return;
                  if (e.key === 'ArrowLeft' && index > 0) {
                    e.preventDefault();
                    onSetActiveSheet(index - 1);
                  } else if (e.key === 'ArrowRight' && index < sheets.length - 1) {
                    e.preventDefault();
                    onSetActiveSheet(index + 1);
                  } else if (e.key === 'Home') {
                    e.preventDefault();
                    onSetActiveSheet(0);
                  } else if (e.key === 'End') {
                    e.preventDefault();
                    onSetActiveSheet(sheets.length - 1);
                  }
                }}
                className={`
                  relative flex items-center gap-1 px-3 h-8 rounded-t-md text-xs font-medium transition-colors
                  ${isActive
                    ? 'bg-background border border-b-0 border-border text-foreground shadow-sm -mb-px z-10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }
                `}
                style={{ minWidth: 60 }}
              >
                {sheet.meta?.color && (
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: sheet.meta.color }} />
                )}

                {hasUp && <ChevronUp className="w-3 h-3 text-primary/60 flex-shrink-0" />}

                {isEditing ? (
                  <input
                    ref={inputRef}
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={e => {
                      if (e.key === 'Enter') commitRename();
                      if (e.key === 'Escape') setEditingIndex(null);
                    }}
                    className="w-28 bg-transparent border-b border-primary text-sm leading-normal outline-none py-0.5"
                    maxLength={30}
                  />
                ) : (
                  <span className="truncate max-w-[140px] text-sm leading-normal py-0.5">{getSheetDisplayName(sheet.name, index)}</span>
                )}

                {hasDown && <ChevronDown className="w-3 h-3 text-primary/60 flex-shrink-0" />}
              </button>
            </ContextMenuTrigger>

            <ContextMenuContent>
              <ContextMenuItem onClick={() => startRename(index)}>
                Rename
              </ContextMenuItem>
              <ContextMenuItem onClick={() => onDuplicateSheet(index)}>
                Duplicate
              </ContextMenuItem>
              <ContextMenuSeparator />
              {index > 0 && (
                <ContextMenuItem onClick={() => onReorderSheets(index, index - 1)}>
                  Move Left
                </ContextMenuItem>
              )}
              {index < sheets.length - 1 && (
                <ContextMenuItem onClick={() => onReorderSheets(index, index + 1)}>
                  Move Right
                </ContextMenuItem>
              )}
              <ContextMenuSeparator />
              <ContextMenuItem
                onClick={() => onRemoveSheet(index)}
                disabled={sheets.length <= 1}
                className="text-destructive focus:text-destructive"
              >
                Delete
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        );
      })}

      {!readOnly && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-md flex-shrink-0 ml-1"
            // Wrap so the React MouseEvent doesn't get passed as the sheet
            // name — addSheet's first arg is `name?: string` and receiving an
            // event object here crashes downstream serialization (autosave,
            // cloud save).
            onClick={() => onAddSheet()}
            disabled={!canAdd}
            aria-label="Add sheet"
            data-guide="sheet-add"
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {canAdd ? 'Add new sheet' : `Maximum ${maxSheets} sheets (upgrade plan)`}
        </TooltipContent>
      </Tooltip>
      )}
    </div>
  );
}
